import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { config } from '../../config';
import { fetchWithCSRF } from '../../utils/apiUtils';

// Asynchronous thunk to fetch member data
export const fetchMembers = createAsyncThunk(
    'members/fetchMembers',
    async ({ type, id, search = '' }, { rejectWithValue }) => {
        try {
            const searchParam = search ? `?search=${encodeURIComponent(search)}` : '';
            const response = await fetchWithCSRF(`${config.API_URI}/v1/members/${type}/${id}${searchParam}`, {
                credentials: 'include',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                }
            });

            if (!response.ok) {
                throw new Error('Failed to fetch member data');
            }

            const data = await response.json();
            return { type, members: data, search, id }; // Include search to track if it's a search result
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

// Asynchronous thunk to add a member (with optimistic UI)
export const addMember = createAsyncThunk(
    'members/addMember',
    async ({ cardId, memberId }, { dispatch, rejectWithValue }) => {
        
        try {
            const response = await fetch(`${config.API_URI}/v1/cards/${cardId}/addMember`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    
                },
                body: JSON.stringify({ memberId }),
            });

            if (!response.ok) {
                throw new Error('Failed to add member');
            }

            const data = await response.json();
            return { cardId, member: data };  // Return added member details
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

// Asynchronous thunk to remove a member (with optimistic UI)
export const removeMember = createAsyncThunk(
    'members/removeMember',
    async ({ cardId, memberId }, { dispatch, rejectWithValue }) => {
        
        try {
            const response = await fetch(`${config.API_URI}/v1/cards/${cardId}/removeMember`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    
                },
                body: JSON.stringify({ memberId }),
            });

            if (!response.ok) {
                throw new Error('Failed to remove member');
            }

            return { cardId, memberId };  // Return the memberId that was removed
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

const memberSlice = createSlice({
    name: 'members',
    initialState: {
        member: null,
        boardMembers: [],
        currentBoardId: null, // Track current board ID for caching
        cardMembers: [],
        workspaceMembers: [],
        workspaceGuests: [],
        status: 'idle',
        error: null,
        optimisticUpdates: {}, // Track optimistic updates
    },
    reducers: {
        // Optimistic UI for adding a member
        addMemberOptimistic: (state, action) => {
            const { cardId, member } = action.payload;
            const memberExists = state.cardMembers.some(existingMember => existingMember._id === member._id);
            if (!memberExists) {
                state.cardMembers.push(member);
                state.optimisticUpdates[member._id] = 'ADD'; // Track optimistic update
            }
        },

        // Optimistic UI for removing a member
        removeMemberOptimistic: (state, action) => {
            const { cardId, memberId } = action.payload;
            state.cardMembers = state.cardMembers.filter(member => member._id !== memberId);
            state.optimisticUpdates[memberId] = 'REMOVE'; // Track optimistic update
        },

        // Rollback function in case optimistic update fails
        rollbackOptimisticUpdate: (state, action) => {
            const { cardId, member, type } = action.payload;
            if (type === 'ADD') {
                state.cardMembers = state.cardMembers.filter(m => m._id !== member._id); // Revert member addition
            } else if (type === 'REMOVE') {
                state.cardMembers.push(member); // Re-add the removed member
            }
            delete state.optimisticUpdates[member._id]; // Remove optimistic tracking
        },
        memberInfo: (state, action) => {
            state.member = action.payload.member;
        },

        // ========== REAL-TIME OPTIMISTIC ACTIONS ==========

        // Add member from real-time event
        optimisticAddMember: (state, action) => {
            const { type, member } = action.payload;

            if (type === 'card') {
                // Check if member already exists (avoid duplicates)
                const exists = state.cardMembers.some(m => m._id === member._id);
                if (!exists) {
                    state.cardMembers.push(member);
                }
            } else if (type === 'board') {
                const exists = state.boardMembers.some(m => m._id === member._id);
                if (!exists) {
                    state.boardMembers.push(member);
                }
            } else if (type === 'workspace') {
                const exists = state.workspaceMembers.some(m => m._id === member._id);
                if (!exists) {
                    state.workspaceMembers.push(member);
                }
            }
        },

        // Remove member from real-time event
        optimisticRemoveMember: (state, action) => {
            const { type, memberId } = action.payload;

            if (type === 'card') {
                state.cardMembers = state.cardMembers.filter(m => m._id !== memberId);
            } else if (type === 'board') {
                state.boardMembers = state.boardMembers.filter(m => m._id !== memberId);
            } else if (type === 'workspace') {
                state.workspaceMembers = state.workspaceMembers.filter(m => m._id !== memberId);
                state.workspaceGuests = state.workspaceGuests.filter(m => m._id !== memberId);
            }
        },

        // Update member role from real-time event
        optimisticUpdateMemberRole: (state, action) => {
            const { type, memberId, newRole, member } = action.payload;

            if (type === 'workspace') {
                // Remove from both lists first
                state.workspaceMembers = state.workspaceMembers.filter(m => m._id !== memberId);
                state.workspaceGuests = state.workspaceGuests.filter(m => m._id !== memberId);

                // Add to appropriate list based on new role
                if (newRole === 'guest') {
                    // Add to guests list
                    if (member) {
                        state.workspaceGuests.push({ ...member, role: 'guest' });
                    }
                } else {
                    // Add to members list (admin or member role)
                    if (member) {
                        state.workspaceMembers.push({ ...member, role: newRole });
                    }
                }
            } else if (type === 'board') {
                // Update role in board members
                const memberIndex = state.boardMembers.findIndex(m => m._id === memberId);
                if (memberIndex !== -1) {
                    state.boardMembers[memberIndex].role = newRole;
                }
            }
        },

        // Set card members from real-time event (replaces entire list)
        setCardMembersFromSocket: (state, action) => {
            const members = action.payload;
            state.cardMembers = members;
        },

        // Set board members from real-time event (replaces entire list)
        setBoardMembersFromSocket: (state, action) => {
            const members = action.payload;
            state.boardMembers = members;
        },

        // Set workspace members from real-time event (replaces entire list)
        setWorkspaceMembersFromSocket: (state, action) => {
            const { members, guests } = action.payload;
            if (members) {
                state.workspaceMembers = members;
            }
            if (guests) {
                state.workspaceGuests = guests;
            }
        },

        // Clear board members
        clearBoardMembers: (state) => {
            state.boardMembers = [];
            state.currentBoardId = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch members lifecycle
            .addCase(fetchMembers.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(fetchMembers.fulfilled, (state, action) => {
                state.status = 'succeeded';
                const { type, members, id } = action.payload;
                if (type === 'board' || type === 'page') {
                    state.boardMembers = members;
                    state.currentBoardId = id;
                } else if (type === 'card') {
                    state.cardMembers = members;  // Update with the fresh list from the server
                } else if (type === 'workspace') {
                    state.workspaceMembers = members;
                } else if (type === 'guests') {
                    state.workspaceGuests = members;
                }
            })
            .addCase(fetchMembers.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload || action.error.message;
            })

            // Add member lifecycle. The server endpoint only returns
            // `{ message: 'Member added to card' }`, NOT the member doc, so
            // we must not treat action.payload.member as a member object —
            // doing so used to push a no-_id phantom into cardMembers on
            // every add. The optimistic add (addMemberOptimistic) already
            // placed the real member in the list at click time; here we
            // just clear the optimistic-tracking flag.
            .addCase(addMember.fulfilled, (state, action) => {
                const { memberId } = action.meta.arg;
                if (memberId) delete state.optimisticUpdates[memberId];
            })
            .addCase(addMember.rejected, (state, action) => {
                const { cardId, memberId } = action.meta.arg;
                const member = state.cardMembers.find(m => m._id === memberId);
                if (member) {
                    // state.cardMembers = state.cardMembers.filter(m => m._id !== member._id);  // Remove member
                    delete state.optimisticUpdates[memberId];  // Clear optimistic tracking
                }
            })

            // Remove member lifecycle
            .addCase(removeMember.fulfilled, (state, action) => {
                const { memberId } = action.payload;
                state.cardMembers = state.cardMembers.filter(m => m._id !== memberId);  // Remove member from card
                delete state.optimisticUpdates[memberId];  // Clear optimistic tracking
            })
            .addCase(removeMember.rejected, (state, action) => {
                const { cardId, memberId } = action.meta.arg;
                const member = state.cardMembers.find(m => m._id === memberId);
                if (member) {
                    // state.cardMembers.push(member);  // Re-add member if removal failed
                    delete state.optimisticUpdates[memberId];  // Clear optimistic tracking
                }
            });
    }
});

// Exporting actions and reducer
export const {
    memberInfo,
    addMemberOptimistic,
    removeMemberOptimistic,
    rollbackOptimisticUpdate,
    // Real-time optimistic actions
    optimisticAddMember,
    optimisticRemoveMember,
    optimisticUpdateMemberRole,
    setCardMembersFromSocket,
    setBoardMembersFromSocket,
    setWorkspaceMembersFromSocket,
    clearBoardMembers,
} = memberSlice.actions;
export default memberSlice.reducer;
